import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLoading } from '../context/LoadingContext';

gsap.registerPlugin(ScrollTrigger);

// Optimized animation variants with reduced complexity
const animationVariants = {
  fadeLeft: {
    initial: { x: -50, opacity: 0 }, // Reduced movement
    animate: { x: 0, opacity: 1 },
    duration: 0.8 // Reduced duration
  },
  fadeRight: {
    initial: { x: 50, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    duration: 0.8
  },
  fadeUp: {
    initial: { y: 30, opacity: 0 }, // Reduced movement
    animate: { y: 0, opacity: 1 },
    duration: 0.8
  },
  scale: {
    initial: { scale: 0.95, opacity: 0 }, // Reduced scale difference
    animate: { scale: 1, opacity: 1 },
    duration: 0.8
  },
  rotate: {
    initial: { rotation: 5, opacity: 0 }, // Reduced rotation
    animate: { rotation: 0, opacity: 1 },
    duration: 0.8
  }
};

const AnimatedSection = React.memo(({ 
  children, 
  className = "", 
  animationType = "fadeUp", 
  delay = 0,
  id,
  ...props 
}) => {
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const { isInitialized } = useLoading();

  // Memoize variant configuration
  const variant = useMemo(() => 
    animationVariants[animationType] || animationVariants.fadeUp, 
    [animationType]
  );

  // Optimized animation creation
  const createAnimation = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Clear existing timeline
    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    // If the section is already in the viewport when the animation is created
    // we skip the intro animation and show it immediately. This prevents the
    // element from flashing invisible (opacity: 0) when users refresh the
    // page while scrolled near the bottom or quickly scroll past the fold
    // before `isInitialized` becomes true.
    if (ScrollTrigger.isInViewport(section, 0.2)) {
      gsap.set(section, {
        ...variant.animate,
        force3D: true,
        transformOrigin: "center center"
      });
      return;
    }

    // Set initial state with GPU acceleration
    gsap.set(section, {
      ...variant.initial,
      force3D: true,
      transformOrigin: "center center"
    });

    // Create optimized timeline
    timelineRef.current = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 90%", // Start a little earlier to ensure visibility
        toggleActions: "play none none reverse",
        once: false, // Allow replay for better performance
        fastScrollEnd: true, // Better scroll performance
        preventOverlaps: true
      }
    });

    // Main animation
    timelineRef.current.to(section, {
      ...variant.animate,
      duration: variant.duration,
      delay,
      ease: "power2.out"
    });

    // Simplified background color change
    if (section.dataset.bgColor) {
      timelineRef.current.add(() => {
        document.body.style.backgroundColor = section.dataset.bgColor;
      }, 0);
    }

    // Reduced parallax effect for performance
    const parallaxAmount = parseFloat(section.dataset.speed) || 0.05; // Much smaller parallax
    if (parallaxAmount > 0) {
      gsap.to(section, {
        y: () => parallaxAmount * window.innerHeight * -0.2, // Reduced parallax intensity
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          invalidateOnRefresh: true
        }
      });
    }
  }, [variant, delay]);

  useEffect(() => {
    if (!isInitialized) return;

    // Use requestIdleCallback for non-critical animations
    const initAnimation = () => {
      createAnimation();
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(initAnimation);
    } else {
      setTimeout(initAnimation, 50);
    }

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      // Clean up only this component's ScrollTriggers
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === sectionRef.current) {
          trigger.kill();
        }
      });
    };
  }, [isInitialized, createAnimation]);

  return (
    <section 
      ref={sectionRef} 
      className={className}
      id={id}
      data-speed={0.05} // Default reduced parallax speed
      style={{
        willChange: 'transform, opacity',
        transform: 'translate3d(0,0,0)' // GPU acceleration hint
      }}
      {...props}
    >
      {children}
    </section>
  );
});

AnimatedSection.displayName = 'AnimatedSection';

export default AnimatedSection; 